// اجزای مشترک AP-INS-01 (SCR-STAFF-850، 851، 852 و COMP-PATIENT-COVERAGE): کلیدهای Query، فهرست‌های شمارشی، رنگ وضعیت و نمایش
// علت صفر بودن سهم بیمه. علت صفر همیشه با متن i18n نمایش داده می‌شود، چون «بیمه چرا نگرفت؟» پرسش روزمره صندوق و پذیرش است
// (AC-INS-040.2).
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import type {
  InsCapKind,
  InsCapScope,
  InsClaimSubmissionMethod,
  InsContractStatus,
  InsCoverageVerification,
  InsEligibilityResult,
  InsInsuredRole,
  InsInsurerKind,
  InsManualEvidenceKind,
  InsPreAuthorizationStatus,
  InsReferralStatus,
  InsReferralUsage,
  InsRequiredDocumentKind,
  InsTariffBasis,
  InsZeroShareReason,
} from '@phoenix/api-client';
import { useTranslation } from '@phoenix/i18n';
import { StatusChip, faDigits, type StatusTone } from '@phoenix/ui';

export const INSURERS_PATH = '/insurance/insurers';
export const PLANS_PATH = '/insurance/plans';
export const AUTHORIZATIONS_PATH = '/insurance/authorizations';

export const INSURER_KINDS: readonly InsInsurerKind[] = ['Basic', 'Supplementary', 'OrganizationalFund', 'Tpa'];
export const TARIFF_BASES: readonly InsTariffBasis[] = ['ApprovedTariff', 'AgreedTariffList'];
export const SUBMISSION_METHODS: readonly InsClaimSubmissionMethod[] = [
  'NationalEPrescription',
  'Api',
  'FileExport',
  'Paper',
];
export const DOCUMENT_KINDS: readonly InsRequiredDocumentKind[] = [
  'DiagnosisCode',
  'Prescription',
  'ReferralLetter',
  'PreAuthorization',
  'InsuredSignature',
  'IdentityCard',
  'ProcedureReport',
  'LabResult',
  'ConsumablesInvoice',
];
export const CAP_KINDS: readonly InsCapKind[] = [
  'PerLine',
  'PerVisit',
  'CategoryAnnual',
  'SessionCountAnnual',
  'TotalAnnual',
];
export const CAP_SCOPES: readonly InsCapScope[] = ['Insured', 'Family'];
export const INSURED_ROLES: readonly InsInsuredRole[] = ['Principal', 'Dependent'];
export const EVIDENCE_KINDS: readonly InsManualEvidenceKind[] = ['InsuranceCard', 'ReferralLetter', 'InsurerCall'];
export const REFERRAL_USAGES: readonly InsReferralUsage[] = ['SingleUse', 'MultiUse'];

/** کلیدهای Query ماژول بیمه (به ازای مستأجر تا تغییر مستأجر Cache را جابه‌جا نکند). */
export const insuranceKeys = {
  insurers: (tenantId: string | undefined) => ['ins', 'insurers', tenantId] as const,
  contracts: (tenantId: string | undefined, insurerId?: string) => ['ins', 'contracts', tenantId, insurerId ?? null] as const,
  plans: (tenantId: string | undefined, insurerId?: string) => ['ins', 'plans', tenantId, insurerId ?? null] as const,
  plan: (tenantId: string | undefined, planId: string) => ['ins', 'plan', tenantId, planId] as const,
  coverages: (tenantId: string | undefined, patientId: string) => ['ins', 'coverages', tenantId, patientId] as const,
  preAuthorizations: (tenantId: string | undefined, patientId: string) => ['ins', 'preauth', tenantId, patientId] as const,
  referrals: (tenantId: string | undefined, patientId: string) => ['ins', 'referrals', tenantId, patientId] as const,
};

/** درصد از پایه‌امتیاز (۷۰۰۰ = ۷۰٪) با ارقام فارسی. */
export function percentOf(basisPoints: number | string | undefined): string {
  return faDigits(Number(basisPoints ?? 0) / 100);
}

/** پایه‌امتیاز از درصد ورودی کاربر. */
export function toBasisPoints(percent: number): number {
  return Math.round(percent * 100);
}

export function contractTone(status: InsContractStatus | undefined): StatusTone {
  switch (status) {
    case 'Active':
      return 'active';
    case 'Expired':
      return 'inactive';
    default:
      return 'pending';
  }
}

export function verificationTone(verification: InsCoverageVerification | undefined): StatusTone {
  switch (verification) {
    case 'Verified':
      return 'active';
    case 'Revoked':
      return 'error';
    default:
      return 'pending';
  }
}

export function eligibilityTone(result: InsEligibilityResult | undefined): StatusTone {
  switch (result) {
    case 'Eligible':
      return 'active';
    case 'NotEligible':
      return 'error';
    default:
      return 'pending';
  }
}

export function preAuthTone(status: InsPreAuthorizationStatus | undefined): StatusTone {
  switch (status) {
    case 'Approved':
      return 'active';
    case 'PartiallyApproved':
      return 'pending';
    case 'Rejected':
      return 'error';
    case 'Expired':
    case 'Consumed':
      return 'inactive';
    default:
      return 'pending';
  }
}

export function referralTone(status: InsReferralStatus | undefined): StatusTone {
  switch (status) {
    case 'Active':
      return 'active';
    default:
      return 'inactive';
  }
}

/**
 * نشان علت‌های صفر بودن سهم بیمه (AC-INS-040.2): هر علت یک نشان با متن i18n و توضیح کامل در Tooltip، تا کاربر صندوق بداند چرا بیمه
 * سهمی نگرفت و چه کاری ممکن است لازم باشد (مثل گرفتن مجوز قبلی یا تأیید استحقاق).
 */
export function ZeroReasons({ reasons }: { reasons: readonly (InsZeroShareReason | null)[] | undefined }) {
  const { t } = useTranslation('ins');
  const items = (reasons ?? []).filter((reason): reason is NonNullable<InsZeroShareReason> => Boolean(reason));

  if (items.length === 0) {
    return null;
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap data-testid="ins-zero-reasons">
      {items.map((reason) => (
        <Tooltip key={reason} title={t(`ins:zero_reason_hint.${reason}`)}>
          <span data-testid={`ins-zero-reason-${reason}`}>
            <StatusChip tone="pending" labelKey={`ins:zero_reason.${reason}`} />
          </span>
        </Tooltip>
      ))}
    </Stack>
  );
}
